import { failureFromResponse } from '@signalvote/shared';
import type {
  BankSourceProfileDto,
  UpdateBankSourceProfileRequest,
  UpdateBankSourceProfileResult,
} from '@signalvote/shared';
import { skip } from '@signalvote/data';
import { campaignHeaders, defineQuery, defineWrite } from '@/lib/data';
import { apiJson } from './apiFetch';

// ============================================================================
// PPR-017 — Edit bank profile (future imports only)
// ============================================================================

const bankSourceProfileRead = defineQuery({
  key: (profileId: string) => ['finance', 'bank-source-profiles', profileId],
  reads: 'GET /api/finance/bank-source-profiles/{id}',
  fetch: (profileId, { signal, campaignId }) =>
    apiJson<BankSourceProfileDto>(`/api/finance/bank-source-profiles/${profileId}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to load bank profile',
    }),
});

export function useBankSourceProfile(profileId: string | null) {
  return bankSourceProfileRead(profileId ?? skip);
}

const useUpdateBankSourceProfileWrite = defineWrite({
  send: ({ profileId, request }: { profileId: string; request: UpdateBankSourceProfileRequest }) =>
    apiJson<UpdateBankSourceProfileResult>(`/api/finance/bank-source-profiles/${profileId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      json: request,
      // The server's sentence says why (a pre-shipped profile is read-only), so it wins over the fallback.
      error: (body, response) => failureFromResponse(response, body, body?.error || 'Failed to update bank profile'),
    }),
});

export function useUpdateBankSourceProfile() {
  const { mutateAsync, isPending } = useUpdateBankSourceProfileWrite();
  return { updateProfile: mutateAsync, isPending };
}
